// frontend/src/components/booking-management/TripAssignmentDialog.js

import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { User, Truck, Map, Loader2, X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import axios from '../../axiosConfig';

export const TripAssignmentDialog = ({
    isOpen,
    onClose,
    trip,
    entityId,
    onSave
}) => {
    const { t } = useTranslation();
    const { background, text, border, components, semantic, cn } = useTheme();

    // State
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [routeTypes, setRouteTypes] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState(null);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [selectedRouteType, setSelectedRouteType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [assignmentConflict, setAssignmentConflict] = useState(null);

    // Load data on open
    useEffect(() => {
        if (isOpen && trip && entityId) {
            loadAssignmentData();
        }
    }, [isOpen, trip, entityId]);

    const loadAssignmentData = async () => {
        try {
            setLoading(true);

            // trip_id is the actual ID from the database (trips.id)
            const tripId = trip.trip_id || trip.id;
            console.log('[TripAssignmentDialog] Loading data for:', { entityId, tripId, routeId: trip.route_id });

            // Set current assignments
            setSelectedDriverId(trip.driver_id || null);
            setSelectedVehicleId(trip.vehicle_id || null);
            setSelectedRouteType(trip.selected_archived_type || null);

            // Fetch available options in parallel
            const [driversRes, vehiclesRes, routeTypesRes] = await Promise.all([
                axios.get(`/api/trips/${entityId}/available-drivers`),
                axios.get(`/api/vehicles/${entityId}`),
                axios.get(`/api/trips/${entityId}/routes/${trip.route_id}/types`)
            ]);

            console.log('[TripAssignmentDialog] API responses:', {
                drivers: driversRes.data,
                vehicles: vehiclesRes.data,
                routeTypes: routeTypesRes.data
            });

            const fetchedDrivers = driversRes.data.drivers || [];
            const fetchedVehicles = vehiclesRes.data.vehicles || vehiclesRes.data || [];
            const fetchedRouteTypes = routeTypesRes.data.routeTypes || [];

            console.log('[TripAssignmentDialog] Parsed data:', {
                driversCount: fetchedDrivers.length,
                drivers: fetchedDrivers,
                vehiclesCount: fetchedVehicles.length,
                vehicles: fetchedVehicles,
                routeTypesCount: fetchedRouteTypes.length,
                routeTypes: fetchedRouteTypes
            });

            setDrivers(fetchedDrivers);
            setVehicles(fetchedVehicles);
            setRouteTypes(fetchedRouteTypes);

        } catch (error) {
            console.error('[TripAssignmentDialog] Error loading assignment data:', error);
            console.error('[TripAssignmentDialog] Error details:', error.response?.data);
            setErrors({ load: 'Failed to load assignment options' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (overrideVehicleConflict = false) => {
        try {
            setSaving(true);
            setErrors({});

            // trip_id is the actual ID from the database (trips.id)
            const tripId = trip.trip_id || trip.id;

            const updates = {
                driver_id: selectedDriverId,
                vehicle_id: selectedVehicleId,
                selected_archived_type: selectedRouteType
            };
            if (overrideVehicleConflict) {
                updates.override_vehicle_conflict = true;
            }

            await axios.put(
                `/api/trips/${entityId}/${tripId}/admin-assign`,
                updates
            );

            setAssignmentConflict(null);
            if (onSave) {
                await onSave();
            }

            onClose();
        } catch (error) {
            console.error('Error saving assignment:', error);
            if (error.response?.data?.code === 'VEHICLE_ACTIVE_TRIP_CONFLICT') {
                setAssignmentConflict({
                    blockingTrips: error.response.data.blockingTrips || []
                });
                return;
            }
            setErrors({
                save: error.response?.data?.error || 'Failed to save assignment'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (!saving) {
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            title={t('assignTrip') || 'Assign Trip'}
            size="md"
            closeOnBackdropClick={!saving}
            footer={
                <div className="flex justify-end gap-2">
                    <button
                        onClick={() => handleSave(false)}
                        disabled={saving || loading}
                        className={cn(
                            "px-4 py-2 rounded flex items-center gap-2",
                            saving || loading
                                ? components.button.disabled
                                : components.button.primary
                        )}
                    >
                        {saving ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Map className="w-5 h-5" />
                        )}
                        {saving ? t('saving') : t('save')}
                    </button>
                    <button
                        onClick={handleCancel}
                        disabled={saving}
                        className={cn(
                            "px-4 py-2 rounded flex items-center gap-2",
                            saving
                                ? components.button.disabled
                                : components.button.secondary
                        )}
                    >
                        <X className="w-5 h-5" />
                        {t('cancel')}
                    </button>
                </div>
            }
        >
            <div className="space-y-4">
                {/* Error Display */}
                {(errors.load || errors.save) && (
                    <div className={cn(
                        "border rounded-lg p-3",
                        semantic.error.background,
                        border.input,
                        semantic.error.text
                    )}>
                        <div className="flex items-start gap-2">
                            <AlertCircle className={cn("w-5 h-5 flex-shrink-0 mt-0.5", semantic.error.accent)} />
                            <p className="text-sm">{errors.load || errors.save}</p>
                        </div>
                    </div>
                )}
                {assignmentConflict && (
                    <div className={cn(
                        "border rounded-lg p-3",
                        "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200"
                    )}>
                        <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div className="space-y-2 text-sm">
                                <p className="font-medium">{t('vehicleConflict.title')}</p>
                                <p>{t('vehicleConflict.message')}</p>
                                {assignmentConflict.blockingTrips?.map((blockingTrip) => (
                                    <p key={blockingTrip.tripId}>
                                        {t('vehicleConflict.blockingTrip', {
                                            tripId: blockingTrip.tripId,
                                            vehicleName: blockingTrip.vehicleName || blockingTrip.vehicleId,
                                            routeName: blockingTrip.routeName || ''
                                        })}
                                    </p>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => handleSave(true)}
                                    disabled={saving}
                                    className={cn(
                                        "px-3 py-2 rounded flex items-center gap-2",
                                        saving ? components.button.disabled : components.button.primary
                                    )}
                                >
                                    {t('vehicleConflict.confirmOverride')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center items-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Driver Selection */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                <User className="w-4 h-4 inline mr-2" />
                                {t('driver')}
                            </label>
                            <select
                                value={selectedDriverId || ''}
                                onChange={(e) => setSelectedDriverId(e.target.value ? parseInt(e.target.value) : null)}
                                className={cn(
                                    "w-full px-3 py-2 rounded border",
                                    border.input,
                                    background.input,
                                    text.primary
                                )}
                                disabled={saving}
                            >
                                <option value="">{t('noDriver') || 'Select Driver'}</option>
                                {drivers.map(driver => (
                                    <option key={driver.id} value={driver.id}>
                                        {driver.first_name} {driver.last_name}
                                    </option>
                                ))}
                            </select>
                            {drivers.length === 0 && (
                                <p className={cn("text-xs mt-1", text.secondary)}>
                                    {t('noDriversAvailable') || 'No drivers available'}
                                </p>
                            )}
                        </div>

                        {/* Vehicle Selection */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                <Truck className="w-4 h-4 inline mr-2" />
                                {t('Vehicle')}
                            </label>
                            <select
                                value={selectedVehicleId || ''}
                                onChange={(e) => setSelectedVehicleId(e.target.value ? parseInt(e.target.value) : null)}
                                className={cn(
                                    "w-full px-3 py-2 rounded border",
                                    border.input,
                                    background.input,
                                    text.primary
                                )}
                                disabled={saving}
                            >
                                <option value="">{t('noVehicle') || 'Select Vehicle'}</option>
                                {vehicles.map(vehicle => (
                                    <option key={vehicle.id} value={vehicle.id}>
                                        {vehicle.vehicle_number} ({t('capacity')}: {vehicle.capacity}, {t('luggageCapacity')}: {vehicle.luggage_capacity})
                                        
                                    </option>
                                ))}
                            </select>
                            {vehicles.length === 0 && (
                                <p className={cn("text-xs mt-1", text.secondary)}>
                                    {t('noVehiclesAvailable') || 'No vehicles available'}
                                </p>
                            )}
                        </div>

                        {/* Reference Route Type Selection */}
                        <div>
                            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                <Map className="w-4 h-4 inline mr-2" />
                                {t('referenceRouteType')}
                            </label>
                            <select
                                value={selectedRouteType || ''}
                                onChange={(e) => setSelectedRouteType(e.target.value || null)}
                                className={cn(
                                    "w-full px-3 py-2 rounded border",
                                    border.input,
                                    background.input,
                                    text.primary
                                )}
                                disabled={saving}
                            >
                                <option value="">{t('noRouteType') || 'Select Route Type'}</option>
                                {routeTypes.map(type => {
                                    // For system type 'representative', use i18n translation
                                    // For custom types, use translatedName or display_name from database
                                    let displayName;
                                    if (type.type_key === 'representative') {
                                        displayName = t('representativeRoute');
                                    } else {
                                        displayName = type.translatedName?.name || type.display_name;
                                    }
                                    return (
                                        <option key={type.type_key} value={type.type_key}>
                                            {type.icon ? `${type.icon} ` : ''}{displayName}
                                        </option>
                                    );
                                })}
                            </select>
                            {routeTypes.length === 0 && (
                                <p className={cn("text-xs mt-1", text.secondary)}>
                                    {t('noRouteTypesAvailable') || 'No route types available. Create one first.'}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default TripAssignmentDialog;
